import { FC, useMemo } from 'react';
import { Source } from 'react-map-gl';
import { Layer, LayerBand, LAYER_ORDER } from 'components/map/ordered-layer';
import { useAtomValue } from 'jotai';
import { tcUnitSpacingConfigsAtom } from 'pages/type-curves/type-curve-filters/atoms';
import { spacingEditsAtoms } from 'pages/type-curves/inventory-surface/atoms';
import { typeCurveUnitsVisibilityAtom } from 'pages/type-curves/atoms';
import { builderSetbackAtom, builderSelectedFormationAtom } from './atoms';
import { mergeOuterRings } from './compose-boundary';
import { unitSetbackInnerRings } from './ltd-setback-ring';

interface TcSetbackRingLayerProps {
  /** The unit's current shape pieces (outer rings), `[lng, lat]`. */
  pieces: GeoJSON.Position[][];
  /** Unit azimuth in degrees, or null for a draft with no azimuth yet. */
  azimuthDeg: number | null;
}

const EMPTY_FC: GeoJSON.FeatureCollection = {
  type: 'FeatureCollection',
  features: [],
};

const close = (ring: GeoJSON.Position[]): GeoJSON.Position[] => {
  if (ring.length === 0) return ring;
  const last = ring[ring.length - 1];
  if (ring[0][0] !== last[0] || ring[0][1] !== last[1])
    return [...ring, ring[0]];
  return ring;
};

/**
 * The unit's setback keep-out ring on the builder map.
 *
 * Draws, for each unit piece, the band between the unit boundary and its inner
 * drainable region (the unit polygon with the inner ring as a hole) — shaded
 * orange, updating live as the Cross-line / In-line setback inputs change. The
 * band is anisotropic: the long sides pull in by the Cross-line setback, the
 * ends by the In-line setback (see `unitSetbackInnerRings`). Renders nothing
 * when both setbacks are 0.
 *
 * @param {TcSetbackRingLayerProps} props - The unit pieces and azimuth.
 * @return {JSX.Element} The setback ring source/layers.
 */
export const TcSetbackRingLayer: FC<TcSetbackRingLayerProps> = ({
  pieces,
  azimuthDeg,
}) => {
  // The setback for the SELECTED formation band, straight from the spacing API
  // (the same source as the top-row spacing controls) + any live builder edit —
  // so the ring follows the ↑/↓ arrows and edits, decoupled from the gun barrel
  // pane. Falls back to the shared `builderSetbackAtom` for a clone/draft with no
  // per-zone config.
  const unitsVisible = useAtomValue(typeCurveUnitsVisibilityAtom);
  const selectedFormation = useAtomValue(builderSelectedFormationAtom);
  const spacingConfigs = useAtomValue(tcUnitSpacingConfigsAtom);
  const spacingEdits = useAtomValue(spacingEditsAtoms.builder);
  const fallback = useAtomValue(builderSetbackAtom);
  const pkgId = selectedFormation?.inventoryFormationPackageId ?? null;
  const source =
    pkgId !== null
      ? (spacingEdits[pkgId] ??
        (spacingConfigs ?? []).find(
          (c) => c.inventoryFormationPackageId === pkgId
        ))
      : undefined;
  const crossLineSetbackFt = source
    ? source.crossLineSetbackFt
    : fallback.crossLineSetbackFt;
  const inLineSetbackFt = source
    ? source.inLineSetbackFt
    : fallback.inLineSetbackFt;

  const featureCollection = useMemo<GeoJSON.FeatureCollection>(() => {
    const crossline = crossLineSetbackFt ?? 0;
    const inline = inLineSetbackFt ?? 0;
    if ((crossline <= 0 && inline <= 0) || pieces.length === 0) return EMPTY_FC;

    // The app's along-azimuth vector in (east, north) is (sin, cos); the ring
    // math expects the long axis as (cos, sin), so rotate by 90°.
    const azimuthRad = Math.PI / 2 - ((azimuthDeg ?? 0) * Math.PI) / 180;

    // Inset the FINAL outline, not each piece. Pieces that adjoin are one unit,
    // so computing per piece drew a keep-out band down the seam between them —
    // an interior edge that does not exist on the shape being saved. Merging
    // first dissolves those seams; pieces that genuinely do not touch survive as
    // separate rings and still get their own ring each.
    const outlines = mergeOuterRings(pieces);

    const features: GeoJSON.Feature[] = [];
    for (const piece of outlines) {
      if (piece.length < 3) continue;
      const inner = unitSetbackInnerRings(piece, azimuthRad, inline, crossline);
      if (!inner) continue;
      features.push({
        type: 'Feature',
        geometry: {
          type: 'Polygon',
          // Unit ring + inner ring(s) as holes → the band shades between them.
          coordinates: [close(piece), ...inner.map(close)],
        },
        properties: {},
      });
    }
    return { type: 'FeatureCollection', features };
  }, [pieces, azimuthDeg, crossLineSetbackFt, inLineSetbackFt]);

  // The band belongs to the unit being edited, so it goes when "Type Curve
  // Units" hides that unit — the page map already draws its per-unit setback
  // bands under the same checkbox. Leaving an orange ring around a unit that is
  // no longer drawn is what made this look half-applied.
  if (!unitsVisible) return null;

  return (
    <LayerBand band={LAYER_ORDER.unitSetback}>
      <Source id="tc-setback-ring" type="geojson" data={featureCollection}>
        <Layer
          id="tc-setback-ring-fill"
          type="fill"
          // 0.3 to match the gun barrel's keep-out band: at 0.15 the tint was
          // too dull to read over the unit fill beneath it.
          paint={{ 'fill-color': '#ff832b', 'fill-opacity': 0.3 }}
        />
        <Layer
          id="tc-setback-ring-line"
          type="line"
          paint={{
            'line-color': '#ff832b',
            'line-width': 1,
            'line-dasharray': [4, 4],
          }}
        />
      </Source>
    </LayerBand>
  );
};

TcSetbackRingLayer.displayName = 'TcSetbackRingLayer';
